import { apiFetch } from "../lib/apiFetch";
import { useState, useCallback, useEffect, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Phone,
  PhoneCall,
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  BrainCircuit,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  User,
  CalendarRange,
  ListFilter,
  X,
  Sparkles,
  Users,
  Play,
  Pause,
  Square,
  Check,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
} from "lucide-react";
import { cn } from "../lib/utils";

interface CallAgenda {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  greetingPrompt: string;
  useKnowledgeBase: boolean;
  ownerId: string;
  createdAt: number;
  updatedAt: number;
}

interface CallLog {
  id: string;
  callSid: string;
  leadId: string;
  leadName?: string;
  phoneNumber: string;
  agendaId: string;
  agendaName: string;
  status: string;
  duration?: number;
  startedAt: number;
  endedAt?: number;
  summary?: string | null;
  transcript?: string | null;
  feedbackRating?: string | null;
  feedbackTags?: string | null;
  feedbackNotes?: string | null;
  feedbackAt?: number | null;
}

interface Lead {
  id: string;
  phoneNumber: string;
  name?: string;
  stage?: string;
}

const SALES_TRIAL_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Sales Trial Conversion",
  description: "AI sales agent for 5-6 day trial → paid conversion. Calls to onboard, support, and close.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet the lead warmly in Hindi or English based on their name. Introduce yourself as a helpful AI assistant from the company. Ask how their trial is going and if they have downloaded the app yet. Keep it warm, short, and conversational.",
  systemPrompt: `You are a friendly, helpful AI sales representative on a phone call. Your goal is to guide the lead through their free trial and help them get value, then close the sale by day 5-6.

Your tasks by call day:
- Day 1-2 (Onboarding): Confirm they have downloaded the app. Walk them through the key features. Solve any setup issues. Leave them excited.
- Day 3-4 (Value Check-in): Ask how they are using it. Share a quick tip or use case relevant to their business. Handle objections warmly.
- Day 5-6 (Conversion): Summarize the value they have experienced. Explain the pricing plans clearly. Handle objections (cost, timing, features). Ask for the sale directly. If they agree, guide them to the payment link.

Rules:
- Speak naturally, not like a robot. Be warm and conversational.
- If the lead speaks Hindi or Hinglish, respond in simple Hinglish (Roman script only, no Devanagari).
- If the lead speaks English, respond in English.
- Keep each response short — 2-3 sentences max.
- Never use markdown, bullet points, or formatting while speaking.
- If the lead says they are busy, politely schedule a callback and end the call.
- If they are ready to pay, say: "I am so glad to hear that! I will send you the payment link on WhatsApp right now."
- When the conversation is fully complete and the lead confirms no more help needed, end with __END_CALL__.`,
};

const QUALIFICATION_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Lead Qualification",
  description: "Qualify inbound leads using your knowledge base Q&A to determine fit and readiness.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet the lead warmly. Introduce yourself as a helpful AI from the company. Tell them you are calling to understand their needs better and see how you can help.",
  systemPrompt: `You are a friendly AI sales qualifier on a phone call. Your job is to understand the lead's needs, qualify them, and set up next steps.

Your tasks:
1. Ask the qualification questions from the knowledge base naturally — one at a time, not like a form.
2. Listen carefully. If they mention a pain point, acknowledge it and dig deeper.
3. Based on their answers, assess if they are a good fit.
4. If qualified: offer a demo or trial. If not a fit: politely explain and suggest alternatives.

Rules:
- Be conversational and warm. Never robotic.
- Match language to the caller: Hindi/Hinglish or English.
- Keep responses under 3 sentences.
- No markdown or formatting while speaking.
- End the call gracefully when done. Say __END_CALL__ when fully complete.`,
};

const FOLLOWUP_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Re-engagement Follow-up",
  description: "Re-engage cold or silent leads to revive interest.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet them by name. Introduce yourself. Mention you are reaching out because it has been a while and you wanted to check if they had any questions or if anything changed in their situation.",
  systemPrompt: `You are a warm, friendly AI sales agent doing a re-engagement call. The lead went cold — your job is to understand why and rekindle their interest.

Your approach:
1. Start with genuine curiosity: "How have things been?" or "Did anything change since we last spoke?"
2. If they lost interest: find the real reason — was it price, timing, or they found another solution?
3. Address the real objection directly. If it was price, share any new plans or offers. If timing, offer to schedule for later.
4. If they are ready to re-engage: move to next steps (trial, demo, payment).

Rules:
- Be empathetic, not pushy. Never be aggressive.
- Match the caller's language (Hindi/Hinglish or English).
- Keep responses under 3 sentences.
- No markdown or formatting while speaking.
- Say __END_CALL__ when the conversation is complete.`,
};

const STATUS_COLORS: Record<string, string> = {
  completed: "text-green-600 bg-green-50",
  "in-progress": "text-blue-600 bg-blue-50",
  initiated: "text-indigo-600 bg-indigo-50",
  ringing: "text-yellow-600 bg-yellow-50",
  failed: "text-red-600 bg-red-50",
  "no-answer": "text-slate-600 bg-slate-50",
  busy: "text-orange-600 bg-orange-50",
};

const STATUS_LABELS: Record<string, string> = {
  completed: "Answered",
  "in-progress": "In Progress",
  initiated: "Initiated",
  ringing: "Ringing",
  failed: "Failed",
  "no-answer": "No Answer",
  busy: "Busy",
};

const CAMPAIGN_STATUS_COLORS: Record<string, string> = {
  active: "text-green-700 bg-green-50 border-green-200",
  paused: "text-amber-700 bg-amber-50 border-amber-200",
  completed: "text-slate-600 bg-slate-50 border-slate-200",
  cancelled: "text-red-600 bg-red-50 border-red-200",
};

interface MassCampaign {
  id: string;
  name?: string;
  agendaId: string;
  agendaName: string;
  totalLeads: number;
  calledCount: number;
  windowStart: string;
  windowEnd: string;
  intervalMinutes: number;
  status: string;
  lastCalledAt?: number;
  createdAt: number;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Agenda Editor ─────────────────────────────────────────────────────────────

function AgendaEditor({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: Partial<CallAgenda>;
  onSave: (data: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    systemPrompt: initial?.systemPrompt || "",
    greetingPrompt: initial?.greetingPrompt || "",
    useKnowledgeBase: initial?.useKnowledgeBase ?? true,
  });

  return (
    <div className="bg-white border rounded-xl shadow-sm p-6 space-y-4">
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Agenda Name</label>
        <input
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
          placeholder="e.g. Sales Trial Conversion"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Description</label>
        <input
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
          placeholder="Brief description of this call's goal"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Opening Greeting Prompt</label>
        <p className="text-xs text-slate-500 mb-1">Tells the AI how to open the call. Keep it short.</p>
        <textarea
          className="w-full h-24 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none"
          placeholder="Greet the lead warmly. Introduce yourself as..."
          value={form.greetingPrompt}
          onChange={(e) => setForm({ ...form, greetingPrompt: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Full System Prompt</label>
        <p className="text-xs text-slate-500 mb-1">Complete instructions for the AI agent during this call.</p>
        <textarea
          className="w-full h-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none font-mono"
          placeholder="You are a friendly AI sales agent..."
          value={form.systemPrompt}
          onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          className="w-4 h-4 accent-green-600"
          checked={form.useKnowledgeBase}
          onChange={(e) => setForm({ ...form, useKnowledgeBase: e.target.checked })}
        />
        <span className="text-sm font-medium text-slate-700">Inject company Knowledge Base into system prompt</span>
      </label>
      <div className="flex gap-2 justify-end pt-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 border rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          disabled={!form.name || !form.systemPrompt || saving}
          onClick={() => onSave(form)}
          className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Agenda
        </button>
      </div>
    </div>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = "leads" | "agendas" | "logs" | "campaigns";

export function Calling() {
  const [tab, setTab] = useState<Tab>("agendas");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string>("");
  const [callingLeadId, setCallingLeadId] = useState<string | null>(null);
  const [callResult, setCallResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [editingAgenda, setEditingAgenda] = useState<CallAgenda | "new" | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [logsViewMode, setLogsViewMode] = useState<"table" | "summaries">("table");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [aiRequirement, setAiRequirement] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiPrefill, setAiPrefill] = useState<Partial<CallAgenda> | null>(null);
  // Feedback panel
  const [feedbackLog, setFeedbackLog] = useState<CallLog | null>(null);
  const [fbRating, setFbRating] = useState<"good" | "needs_work" | null>(null);
  const [fbTags, setFbTags] = useState<Set<string>>(new Set());
  const [fbNotes, setFbNotes] = useState("");
  // Logs filters + sort
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"startedAt" | "leadName" | "status" | "duration">("startedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  // Mass campaign creation
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignLeadSearch, setCampaignLeadSearch] = useState("");
  const [debouncedCampaignSearch, setDebouncedCampaignSearch] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [campaignAgendaId, setCampaignAgendaId] = useState("");
  const [campaignWindowStart, setCampaignWindowStart] = useState("09:00");
  const [campaignWindowEnd, setCampaignWindowEnd] = useState("18:00");
  const [campaignIntervalMinutes, setCampaignIntervalMinutes] = useState(3);
  const [campaignName, setCampaignName] = useState("");
  const [campaignFilterAttendedClass, setCampaignFilterAttendedClass] = useState(false);
  const [campaignFilterAppMonths, setCampaignFilterAppMonths] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const [ownerId, setOwnerId] = useState<string>("");
  useEffect(() => {
    fetch("/api/whatsapp/owner")
      .then((r) => r.json())
      .then((d) => { if (d.ownerId) setOwnerId(d.ownerId); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(leadSearch), 300);
    return () => clearTimeout(t);
  }, [leadSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCampaignSearch(campaignLeadSearch), 300);
    return () => clearTimeout(t);
  }, [campaignLeadSearch]);

  // ── Leads from SQLite (server-side search, 50-lead limit) ──────────────────
  const { data: leads = [] } = useQuery<Lead[]>({
    queryKey: ["leads", ownerId, debouncedSearch],
    queryFn: async () => {
      if (!ownerId) return [];
      const params = new URLSearchParams({ ownerId, limit: "50" });
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await apiFetch(`/api/whatsapp/chats?${params}`);
      if (!res.ok) throw new Error("Failed to fetch leads");
      return res.json();
    },
    enabled: !!ownerId,
  });

  // ── Agendas from SQLite ─────────────────────────────────────────────────────
  const { data: agendas = [], isPending: agendasLoading } = useQuery<CallAgenda[]>({
    queryKey: ["callAgendas", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/calling/agendas?ownerId=${ownerId}`);
      if (!res.ok) throw new Error("Failed to fetch agendas");
      return res.json();
    },
    enabled: !!ownerId,
  });

  // ── Call logs from SQLite ───────────────────────────────────────────────────
  const { data: logs = [] } = useQuery<CallLog[]>({
    queryKey: ["callLogs", ownerId, dateFrom, dateTo, statusFilter],
    queryFn: async () => {
      if (!ownerId) return [];
      const params = new URLSearchParams({ ownerId });
      if (dateFrom) params.set("from", String(new Date(dateFrom).getTime()));
      if (dateTo) params.set("to", String(new Date(dateTo + "T23:59:59").getTime()));
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await apiFetch(`/api/calling/logs?${params}`);
      if (!res.ok) throw new Error("Failed to fetch logs");
      return res.json();
    },
    enabled: !!ownerId && tab === "logs",
    refetchInterval: tab === "logs" ? 8000 : false,
  });

  // ── Mass call campaigns ─────────────────────────────────────────────────────
  const { data: campaigns = [] } = useQuery<MassCampaign[]>({
    queryKey: ["massCampaigns", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/calling/mass-campaigns?ownerId=${ownerId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!ownerId && tab === "campaigns",
    refetchInterval: tab === "campaigns" ? 10000 : false,
  });

  // ── Leads for campaign creation ─────────────────────────────────────────────
  const { data: campaignLeads = [] } = useQuery<Lead[]>({
    queryKey: ["campaignLeads", ownerId, debouncedCampaignSearch, campaignFilterAttendedClass, campaignFilterAppMonths],
    queryFn: async () => {
      if (!ownerId) return [];
      const params = new URLSearchParams({ ownerId, limit: "1000" });
      if (debouncedCampaignSearch) params.set("search", debouncedCampaignSearch);
      if (campaignFilterAttendedClass) params.set("attendedClass", "true");
      if (campaignFilterAppMonths) params.set("appDownloadedMonths", String(campaignFilterAppMonths));
      const res = await apiFetch(`/api/whatsapp/chats?${params}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!ownerId && showCreateCampaign,
  });

  // ── Save agenda (create or update) ─────────────────────────────────────────
  const saveAgendaMutation = useMutation({
    mutationFn: async (data: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => {
      if (editingAgenda && editingAgenda !== "new") {
        const res = await apiFetch(`/api/calling/agendas/${editingAgenda.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, ownerId }),
        });
        if (!res.ok) throw new Error("Failed to update agenda");
      } else {
        const res = await apiFetch("/api/calling/agendas", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, ownerId }),
        });
        if (!res.ok) throw new Error("Failed to create agenda");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callAgendas"] });
      setEditingAgenda(null);
      setAiPrefill(null);
    },
  });

  // ── Delete agenda ───────────────────────────────────────────────────────────
  const deleteAgendaMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/calling/agendas/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete agenda");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["callAgendas"] }),
  });

  // ── Mass campaigns ──────────────────────────────────────────────────────────
  const createCampaignMutation = useMutation({
    mutationFn: async (data: {
      name: string; agendaId: string; leadIds: string[];
      windowStart: string; windowEnd: string; intervalMinutes: number;
    }) => {
      const res = await apiFetch("/api/calling/mass-campaigns", {
        method: "POST",
        body: JSON.stringify({ ...data, ownerId }),
      });
      if (!res.ok) throw new Error("Failed to create campaign");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["massCampaigns"] });
      setShowCreateCampaign(false);
      setSelectedLeadIds(new Set());
      setCampaignAgendaId("");
      setCampaignName("");
    },
  });

  const updateCampaignMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await apiFetch(`/api/calling/mass-campaigns/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update campaign");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["massCampaigns"] }),
  });

  // ── Seed template ───────────────────────────────────────────────────────────
  const saveFeedbackMutation = useMutation({
    mutationFn: async ({ id, rating, tags, notes }: { id: string; rating: string | null; tags: string[]; notes: string }) => {
      const res = await apiFetch(`/api/calling/logs/${id}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, tags, notes }),
      });
      if (!res.ok) throw new Error("Failed to save feedback");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callLogs"] });
      setFeedbackLog(null);
    },
  });

  const seedTemplate = useCallback(
    async (template: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => {
      await apiFetch("/api/calling/agendas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...template, ownerId }),
      });
      queryClient.invalidateQueries({ queryKey: ["callAgendas"] });
    },
    [ownerId, queryClient]
  );

  // ── Initiate call ───────────────────────────────────────────────────────────
  const handleCall = useCallback(async () => {
    if (!selectedLead || !selectedAgendaId) return;
    setCallingLeadId(selectedLead.id);
    setCallResult(null);
    try {
      const resp = await apiFetch("/api/calling/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId: selectedLead.id, agendaId: selectedAgendaId, ownerId }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setCallResult({ ok: true, msg: `Call initiated! SID: ${data.callSid}` });
        queryClient.invalidateQueries({ queryKey: ["callLogs"] });
      } else {
        setCallResult({ ok: false, msg: data.error || "Failed to initiate call" });
      }
    } catch {
      setCallResult({ ok: false, msg: "Server error. Check CRM connection." });
    } finally {
      setCallingLeadId(null);
    }
  }, [selectedLead, selectedAgendaId, ownerId, queryClient]);

  const handleAiGenerate = useCallback(async () => {
    if (!aiRequirement.trim()) return;
    setAiGenerating(true);
    setAiError(null);
    try {
      const res = await apiFetch("/api/calling/agendas/generate", {
        method: "POST",
        body: JSON.stringify({ requirement: aiRequirement }),
      });
      const data = await res.json();
      if (!res.ok || !data.agenda) throw new Error(data.error || "Generation failed");
      setAiPrefill(data.agenda);
      setEditingAgenda("new");
      setAiGenerateOpen(false);
      setAiRequirement("");
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "Generation failed");
    } finally {
      setAiGenerating(false);
    }
  }, [aiRequirement]);

  // ── Sorted logs ─────────────────────────────────────────────────────────────
  const handleSort = useCallback((col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  }, [sortBy]);

  const sortedLogs = [...(Array.isArray(logs) ? logs : [])].sort((a, b) => {
    let av: string | number = 0, bv: string | number = 0;
    if (sortBy === "leadName") { av = (a.leadName || "").toLowerCase(); bv = (b.leadName || "").toLowerCase(); }
    else if (sortBy === "status") { av = a.status; bv = b.status; }
    else if (sortBy === "duration") { av = a.duration ?? 0; bv = b.duration ?? 0; }
    else { av = a.startedAt; bv = b.startedAt; }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Phone className="w-6 h-6 text-green-600" />
          <h1 className="text-xl font-bold text-slate-900">AI Calling</h1>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
            Powered by Exotel + Gemini
          </span>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {(["leads", "agendas", "logs", "campaigns"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-all",
                tab === t
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              {t === "logs" ? "Call Logs" : t === "agendas" ? "Agendas" : t === "campaigns" ? "Mass Calling" : "Call Leads"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">

        {/* ─── Call Leads Tab ─── */}
        {tab === "leads" && (
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Lead Selector */}
              <div className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  Select Lead
                </h2>
                <input
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500"
                  placeholder="Search by name or phone... (showing top 50)"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                />
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {leads.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No leads found</p>
                  )}
                  {leads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all",
                        selectedLead?.id === lead.id
                          ? "bg-green-50 border border-green-200 text-green-900"
                          : "hover:bg-slate-50 border border-transparent"
                      )}
                    >
                      <p className="font-medium">{lead.name || "Unnamed"}</p>
                      <p className="text-xs text-slate-500">{lead.phoneNumber}</p>
                      {lead.stage && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mt-1 inline-block">
                          {lead.stage}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Agenda Selector */}
              <div className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Select Call Agenda
                </h2>
                {agendas.length === 0 && !agendasLoading ? (
                  <div className="text-center py-6 space-y-3">
                    <p className="text-sm text-slate-500">No agendas yet.</p>
                    <button
                      onClick={() => setTab("agendas")}
                      className="text-sm text-green-600 font-medium hover:underline"
                    >
                      Create an agenda →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {agendas.map((agenda) => (
                      <button
                        key={agenda.id}
                        onClick={() => setSelectedAgendaId(agenda.id)}
                        className={cn(
                          "w-full text-left px-3 py-3 rounded-lg text-sm border transition-all",
                          selectedAgendaId === agenda.id
                            ? "bg-green-50 border-green-200 text-green-900"
                            : "hover:bg-slate-50 border-slate-100"
                        )}
                      >
                        <p className="font-medium">{agenda.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{agenda.description}</p>
                        {agenda.useKnowledgeBase && (
                          <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded mt-1 inline-flex items-center gap-1">
                            <BrainCircuit className="w-2.5 h-2.5" /> Uses KB
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Call Button */}
            <div className="bg-white border rounded-xl shadow-sm p-5">
              {callResult && (
                <div
                  className={cn(
                    "mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2",
                    callResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                  )}
                >
                  {callResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {callResult.msg}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {selectedLead && selectedAgendaId ? (
                    <>
                      Calling{" "}
                      <span className="font-semibold text-slate-900">
                        {selectedLead.name || selectedLead.phoneNumber}
                      </span>{" "}
                      with agenda{" "}
                      <span className="font-semibold text-slate-900">
                        {agendas.find((a) => a.id === selectedAgendaId)?.name}
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-400">Select a lead and agenda above to start a call</span>
                  )}
                </div>
                <button
                  disabled={!selectedLead || !selectedAgendaId || !!callingLeadId}
                  onClick={handleCall}
                  className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 flex items-center gap-2 shadow-sm transition-all"
                >
                  {callingLeadId ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <PhoneCall className="w-4 h-4" />
                  )}
                  {callingLeadId ? "Initiating Call..." : "Start AI Call"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Agendas Tab ─── */}
        {tab === "agendas" && (
          <div className="max-w-3xl mx-auto p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Call Agendas</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => { setAiGenerateOpen(true); setEditingAgenda(null); setAiError(null); }}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-1.5"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate with AI
                </button>
                <button
                  onClick={() => { setEditingAgenda("new"); setAiPrefill(null); setAiGenerateOpen(false); }}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  New Agenda
                </button>
              </div>
            </div>

            {/* AI Generate Panel */}
            {aiGenerateOpen && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-indigo-900">Generate Agenda with AI</h3>
                  </div>
                  <button
                    onClick={() => setAiGenerateOpen(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Describe what this call should achieve — AI will write the full agenda, opening, and AI instructions.
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Convert trial users to paid plan",
                    "Qualify new inbound leads",
                    "Re-engage cold leads",
                    "Collect feedback after first class",
                  ].map((ex) => (
                    <button
                      key={ex}
                      onClick={() => { setAiRequirement(ex); setAiError(null); }}
                      className="text-xs bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1 rounded-full hover:border-indigo-400 transition-colors"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
                <textarea
                  className="w-full h-28 bg-white border border-indigo-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none"
                  placeholder="e.g. I want to call leads who just finished a 7-day free trial and convert them to the annual plan. The AI should ask about their experience, handle price objections warmly, and pitch the ₹2999/year plan."
                  value={aiRequirement}
                  onChange={(e) => { setAiRequirement(e.target.value); setAiError(null); }}
                />
                {aiError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> {aiError}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setAiGenerateOpen(false)}
                    className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!aiRequirement.trim() || aiGenerating}
                    onClick={handleAiGenerate}
                    className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-2"
                  >
                    {aiGenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {aiGenerating ? "Generating..." : "Generate Agenda"}
                  </button>
                </div>
              </div>
            )}

            {/* Template Seeds — only when no agendas and no panels open */}
            {agendas.length === 0 && !agendasLoading && editingAgenda === null && !aiGenerateOpen && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 space-y-3">
                <p className="text-sm font-semibold text-indigo-900">Quick start with a template:</p>
                <div className="grid grid-cols-3 gap-3">
                  {[SALES_TRIAL_AGENDA_TEMPLATE, QUALIFICATION_AGENDA_TEMPLATE, FOLLOWUP_AGENDA_TEMPLATE].map(
                    (tpl) => (
                      <button
                        key={tpl.name}
                        onClick={() => seedTemplate(tpl)}
                        className="text-left p-3 bg-white border border-indigo-100 rounded-lg hover:border-indigo-300 transition-all"
                      >
                        <p className="text-sm font-semibold text-indigo-800">{tpl.name}</p>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tpl.description}</p>
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Editor — pre-filled with AI values when aiPrefill is set */}
            {editingAgenda !== null && (
              <AgendaEditor
                initial={editingAgenda === "new" ? (aiPrefill ?? undefined) : editingAgenda}
                onSave={(data) => saveAgendaMutation.mutate(data)}
                onCancel={() => { setEditingAgenda(null); setAiPrefill(null); }}
                saving={saveAgendaMutation.isPending}
              />
            )}

            {agendasLoading && agendas.length === 0 && (
              <div className="text-center py-12 text-slate-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading agendas...
              </div>
            )}

            {agendas.map((agenda) => (
              <div key={agenda.id} className="bg-white border rounded-xl shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{agenda.name}</h3>
                      {agenda.useKnowledgeBase && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <BrainCircuit className="w-2.5 h-2.5" /> Uses KB
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{agenda.description}</p>
                    <details className="mt-2 group">
                      <summary className="text-xs text-indigo-600 cursor-pointer hover:underline select-none flex items-center gap-1">
                        View prompts
                        <ChevronDown className="w-3 h-3 group-open:hidden" />
                        <ChevronUp className="w-3 h-3 hidden group-open:block" />
                      </summary>
                      <div className="mt-2 space-y-2 text-xs text-slate-600">
                        <div>
                          <span className="font-semibold text-slate-700">Greeting: </span>
                          {agenda.greetingPrompt}
                        </div>
                        <div>
                          <span className="font-semibold text-slate-700">System: </span>
                          <pre className="whitespace-pre-wrap font-sans leading-relaxed mt-1 text-slate-500 bg-slate-50 rounded p-2 max-h-32 overflow-y-auto">
                            {agenda.systemPrompt}
                          </pre>
                        </div>
                      </div>
                    </details>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => { setEditingAgenda(agenda); setAiPrefill(null); }}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${agenda.name}"?`)) deleteAgendaMutation.mutate(agenda.id);
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── Call Logs Tab ─── */}
        {tab === "logs" && (
          <div className="max-w-5xl mx-auto p-6 space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                Call History
                {logs.length > 0 && (
                  <span className="text-xs font-normal text-slate-400">({logs.length} calls)</span>
                )}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Status filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-sm bg-white border rounded-lg px-2 py-1.5 text-slate-700 outline-none focus:border-green-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="completed">Answered</option>
                  <option value="no-answer">No Answer</option>
                  <option value="busy">Busy</option>
                  <option value="failed">Failed</option>
                  <option value="initiated">Initiated</option>
                  <option value="in-progress">In Progress</option>
                </select>
                {/* Date range picker */}
                <div className="flex items-center gap-1.5 bg-white border rounded-lg px-3 py-1.5">
                  <CalendarRange className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="text-sm text-slate-700 outline-none bg-transparent w-32"
                  />
                  <span className="text-slate-300 text-sm">–</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="text-sm text-slate-700 outline-none bg-transparent w-32"
                  />
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="text-slate-400 hover:text-slate-600 ml-1"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {/* View mode toggle */}
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setLogsViewMode("table")}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-md transition-all",
                      logsViewMode === "table"
                        ? "bg-white shadow-sm text-slate-900"
                        : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    Table
                  </button>
                  <button
                    onClick={() => setLogsViewMode("summaries")}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-md transition-all flex items-center gap-1",
                      logsViewMode === "summaries"
                        ? "bg-white shadow-sm text-slate-900"
                        : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    <ListFilter className="w-3 h-3" />
                    Summaries
                  </button>
                </div>
              </div>
            </div>

            {logs.length === 0 ? (
              <div className="bg-white border rounded-xl p-12 text-center text-slate-400 text-sm">
                {dateFrom || dateTo
                  ? "No calls in the selected date range."
                  : "No calls yet. Start a call from the Call Leads tab."}
              </div>
            ) : logsViewMode === "table" ? (
              /* ── Table view with expandable summary rows ── */
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {(["leadName", "status", "duration", "startedAt"] as const).map((col) => {
                        const labels: Record<string, string> = { leadName: "Lead", status: "Status", duration: "Duration", startedAt: "Started" };
                        const active = sortBy === col;
                        return (
                          <th
                            key={col}
                            className="px-4 py-3 cursor-pointer select-none hover:text-slate-800 whitespace-nowrap"
                            onClick={() => handleSort(col)}
                          >
                            <span className="inline-flex items-center gap-1">
                              {labels[col]}
                              <span className={cn("transition-opacity", active ? "opacity-100" : "opacity-0 group-hover:opacity-50")}>
                                {active ? (sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />) : <ChevronDown className="w-3 h-3 inline opacity-30" />}
                              </span>
                            </span>
                          </th>
                        );
                      })}
                      <th className="px-4 py-3">Agenda</th>
                      <th className="px-4 py-3 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedLogs.map((log) => (
                      <Fragment key={log.id}>
                        <tr
                          className={cn(
                            "transition-colors",
                            log.summary ? "cursor-pointer hover:bg-slate-50" : "hover:bg-slate-50",
                            expandedLogId === log.id && "bg-slate-50"
                          )}
                          onClick={() =>
                            log.summary &&
                            setExpandedLogId(expandedLogId === log.id ? null : log.id)
                          }
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{log.leadName || "—"}</p>
                            <p className="text-xs text-slate-500">{log.phoneNumber}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                "px-2 py-1 rounded-full text-xs font-medium capitalize",
                                STATUS_COLORS[log.status] || "text-slate-600 bg-slate-50"
                              )}
                            >
                              {STATUS_LABELS[log.status] || log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{formatDuration(log.duration)}</td>
                          <td className="px-4 py-3 text-slate-500">{formatTime(log.startedAt)}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{log.agendaName}</td>
                          <td className="px-4 py-3 text-slate-400">
                            <div className="flex items-center gap-1">
                              <button
                                title="Give feedback on this call"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFeedbackLog(log);
                                  setFbRating(log.feedbackRating as "good" | "needs_work" | null ?? null);
                                  setFbTags(new Set(log.feedbackTags ? JSON.parse(log.feedbackTags) : []));
                                  setFbNotes(log.feedbackNotes || "");
                                }}
                                className={cn(
                                  "p-1 rounded hover:bg-slate-100 transition-colors",
                                  log.feedbackRating ? "text-green-600" : "text-slate-300 hover:text-slate-500"
                                )}
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                              </button>
                              {log.summary &&
                                (expandedLogId === log.id ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                ))}
                            </div>
                          </td>
                        </tr>
                        {expandedLogId === log.id && log.summary && (
                          <tr className="bg-indigo-50/40">
                            <td colSpan={6} className="px-5 py-4">
                              <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1">
                                <BrainCircuit className="w-3.5 h-3.5" /> AI Call Summary
                              </p>
                              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                {log.summary}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ── Summaries view ── */
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="bg-white border rounded-xl shadow-sm p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-slate-900">{log.leadName || log.phoneNumber}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {log.agendaName} · {formatTime(log.startedAt)} · {formatDuration(log.duration)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium capitalize",
                            STATUS_COLORS[log.status] || "text-slate-600 bg-slate-50"
                          )}
                        >
                          {log.status}
                        </span>
                        <button
                          title="Give feedback on this call"
                          onClick={() => {
                            setFeedbackLog(log);
                            setFbRating(log.feedbackRating as "good" | "needs_work" | null ?? null);
                            setFbTags(new Set(log.feedbackTags ? JSON.parse(log.feedbackTags) : []));
                            setFbNotes(log.feedbackNotes || "");
                          }}
                          className={cn(
                            "p-1.5 rounded-lg border transition-colors",
                            log.feedbackRating
                              ? "border-green-300 text-green-600 bg-green-50"
                              : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
                          )}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {log.summary ? (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1">
                          <BrainCircuit className="w-3.5 h-3.5" /> AI Summary
                        </p>
                        <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                          {log.summary}
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-slate-400 italic">
                        {log.status === "completed"
                          ? "Summary generating — check back shortly."
                          : "No summary available for this call."}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Mass Calling Tab ─── */}
        {tab === "campaigns" && (
          <div className="max-w-3xl mx-auto p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" /> Mass Calling Campaigns
              </h2>
              <button
                onClick={() => setShowCreateCampaign(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4" /> New Campaign
              </button>
            </div>

            {/* Create Campaign Panel */}
            {showCreateCampaign && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
                <h4 className="font-semibold text-slate-800">Create Campaign</h4>

                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name</label>
                  <input
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                    placeholder="e.g. June Trial Follow-up"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                </div>

                {/* Select Agenda */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Agenda</label>
                  {agendasLoading ? (
                    <p className="text-sm text-slate-400">Loading agendas…</p>
                  ) : agendas.length === 0 ? (
                    <p className="text-sm text-slate-400">No agendas yet — create one in the Agendas tab first.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {agendas.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => setCampaignAgendaId(a.id)}
                          className={cn(
                            "text-left px-3 py-2 rounded-lg border text-sm transition-colors",
                            campaignAgendaId === a.id
                              ? "border-green-500 bg-green-50 text-green-800"
                              : "border-slate-200 hover:border-slate-300 text-slate-700"
                          )}
                        >
                          <p className="font-medium">{a.name}</p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{a.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Select Leads */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">
                      Select Leads{" "}
                      {selectedLeadIds.size > 0 && (
                        <span className="text-green-600">({selectedLeadIds.size} selected)</span>
                      )}
                    </label>
                    {campaignLeads.length > 0 && (
                      <button
                        onClick={() =>
                          selectedLeadIds.size === campaignLeads.length
                            ? setSelectedLeadIds(new Set())
                            : setSelectedLeadIds(new Set(campaignLeads.map((l: Lead) => l.id)))
                        }
                        className="text-xs text-green-600 hover:underline"
                      >
                        {selectedLeadIds.size === campaignLeads.length ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <button
                      onClick={() => {
                        setCampaignFilterAttendedClass(!campaignFilterAttendedClass);
                        setSelectedLeadIds(new Set());
                      }}
                      className={cn(
                        "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                        campaignFilterAttendedClass
                          ? "bg-green-100 border-green-500 text-green-800"
                          : "border-slate-200 text-slate-500 hover:border-slate-300"
                      )}
                    >
                      Attended ≥1 class
                    </button>
                    {[1, 2, 3].map((m) => (
                      <button
                        key={m}
                        onClick={() => {
                          setCampaignFilterAppMonths(campaignFilterAppMonths === m ? null : m);
                          setSelectedLeadIds(new Set());
                        }}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                          campaignFilterAppMonths === m
                            ? "bg-blue-100 border-blue-500 text-blue-800"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        )}
                      >
                        Downloaded app: last {m}m
                      </button>
                    ))}
                  </div>
                  <input
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 mb-2"
                    placeholder="Search leads by name or phone…"
                    value={campaignLeadSearch}
                    onChange={(e) => setCampaignLeadSearch(e.target.value)}
                  />
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {campaignLeads.length === 0 ? (
                      <p className="text-sm text-slate-400 p-3 text-center">No leads found.</p>
                    ) : (
                      campaignLeads.map((lead: Lead) => (
                        <label
                          key={lead.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-green-600"
                            checked={selectedLeadIds.has(lead.id)}
                            onChange={(e) => {
                              const next = new Set(selectedLeadIds);
                              e.target.checked ? next.add(lead.id) : next.delete(lead.id);
                              setSelectedLeadIds(next);
                            }}
                          />
                          <span className="text-sm text-slate-800 flex-1">{lead.name || "Unnamed"}</span>
                          <span className="text-xs text-slate-400">{lead.phoneNumber}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Time Window & Interval */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Window From (IST)</label>
                    <input
                      type="time"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-green-500"
                      value={campaignWindowStart}
                      onChange={(e) => setCampaignWindowStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Window To (IST)</label>
                    <input
                      type="time"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-green-500"
                      value={campaignWindowEnd}
                      onChange={(e) => setCampaignWindowEnd(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Interval (min)</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-green-500"
                      value={campaignIntervalMinutes}
                      onChange={(e) => setCampaignIntervalMinutes(Number(e.target.value))}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => {
                      if (!campaignAgendaId || selectedLeadIds.size === 0) return;
                      createCampaignMutation.mutate({
                        name: campaignName || "Untitled Campaign",
                        agendaId: campaignAgendaId,
                        leadIds: Array.from(selectedLeadIds),
                        windowStart: campaignWindowStart,
                        windowEnd: campaignWindowEnd,
                        intervalMinutes: campaignIntervalMinutes,
                      });
                    }}
                    disabled={!campaignAgendaId || selectedLeadIds.size === 0 || createCampaignMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createCampaignMutation.isPending ? "Creating…" : "Start Campaign"}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateCampaign(false);
                      setCampaignName("");
                      setCampaignAgendaId("");
                      setSelectedLeadIds(new Set());
                      setCampaignFilterAttendedClass(false);
                      setCampaignFilterAppMonths(null);
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Campaign List */}
            {campaigns.length === 0 ? (
              <div className="text-center py-14 text-slate-400">
                <Users className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No campaigns yet. Create one to start mass calling.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((c: MassCampaign) => (
                  <div key={c.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="font-semibold text-slate-900 truncate">{c.name || "Untitled Campaign"}</p>
                          <span
                            className={cn(
                              "px-2 py-0.5 rounded-full text-xs font-medium border capitalize",
                              CAMPAIGN_STATUS_COLORS[c.status] || "text-slate-600 bg-slate-50 border-slate-200"
                            )}
                          >
                            {c.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          Agenda: <span className="text-slate-700">{c.agendaName}</span>
                          {" · "}Window: {c.windowStart}–{c.windowEnd} IST
                          {" · "}Every {c.intervalMinutes} min
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.status === "active" && (
                          <button
                            onClick={() => updateCampaignMutation.mutate({ id: c.id, status: "paused" })}
                            title="Pause campaign"
                            className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 border border-amber-200 transition-colors"
                          >
                            <Pause className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {c.status === "paused" && (
                          <button
                            onClick={() => updateCampaignMutation.mutate({ id: c.id, status: "active" })}
                            title="Resume campaign"
                            className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 border border-green-200 transition-colors"
                          >
                            <Play className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(c.status === "active" || c.status === "paused") && (
                          <button
                            onClick={() => {
                              if (confirm("Cancel this campaign? Remaining calls will not be placed."))
                                updateCampaignMutation.mutate({ id: c.id, status: "cancelled" });
                            }}
                            title="Cancel campaign"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 border border-red-200 transition-colors"
                          >
                            <Square className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {c.status === "completed" && (
                          <span className="p-1.5 text-green-500">
                            <Check className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>{c.calledCount} / {c.totalLeads} called</span>
                        <span>{c.totalLeads > 0 ? Math.round((c.calledCount / c.totalLeads) * 100) : 0}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div
                          className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${c.totalLeads > 0 ? Math.round((c.calledCount / c.totalLeads) * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── AI Feedback Side Panel ── */}
      {feedbackLog && (() => {
        const transcript: { role: string; text: string }[] = (() => {
          try { return JSON.parse(feedbackLog.transcript || "[]"); } catch { return []; }
        })();
        const ISSUE_TAGS = [
          "Wrong information given", "Missed caller's question", "Interrupted caller",
          "Too long / verbose", "Premature hangup", "Voicemail not detected",
          "Wrong greeting", "Unnatural conversation",
        ];
        const GOOD_TAGS = [
          "Natural conversation", "Correct information", "Good objection handling",
          "Proper greeting", "Followed agenda well", "Smooth closing",
        ];
        return (
          <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={() => setFeedbackLog(null)} />
            <div className="w-[480px] bg-white shadow-2xl flex flex-col h-full overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b flex items-start justify-between shrink-0">
                <div>
                  <p className="font-semibold text-slate-900">AI Call Feedback</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {feedbackLog.leadName || feedbackLog.phoneNumber} · {new Date(feedbackLog.startedAt).toLocaleDateString()} · {formatDuration(feedbackLog.duration)}
                  </p>
                </div>
                <button onClick={() => setFeedbackLog(null)} className="p-1 rounded hover:bg-slate-100 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Transcript */}
                {transcript.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Transcript</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto bg-slate-50 rounded-xl p-3 border border-slate-100">
                      {transcript.map((t, i) => (
                        <div key={i} className={cn("flex gap-2", t.role === "ai" ? "flex-row" : "flex-row-reverse")}>
                          <div className={cn(
                            "px-3 py-2 rounded-xl text-xs leading-relaxed max-w-[85%]",
                            t.role === "ai"
                              ? "bg-green-100 text-green-900"
                              : "bg-white border border-slate-200 text-slate-700"
                          )}>
                            <span className="block font-semibold mb-0.5 opacity-60">{t.role === "ai" ? "AI" : "Caller"}</span>
                            {t.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 italic bg-slate-50 rounded-xl p-4 text-center">No transcript available for this call.</div>
                )}

                {/* Rating */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Overall Rating</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFbRating(fbRating === "good" ? null : "good")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-colors",
                        fbRating === "good"
                          ? "bg-green-100 border-green-500 text-green-800"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <ThumbsUp className="w-4 h-4" /> Good
                    </button>
                    <button
                      onClick={() => setFbRating(fbRating === "needs_work" ? null : "needs_work")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-colors",
                        fbRating === "needs_work"
                          ? "bg-red-100 border-red-400 text-red-800"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <ThumbsDown className="w-4 h-4" /> Needs Work
                    </button>
                  </div>
                </div>

                {/* Issue tags */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Issues Observed</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ISSUE_TAGS.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          const next = new Set(fbTags);
                          next.has(tag) ? next.delete(tag) : next.add(tag);
                          setFbTags(next);
                        }}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                          fbTags.has(tag)
                            ? "bg-red-100 border-red-400 text-red-800"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Positive tags */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">What Went Well</p>
                  <div className="flex flex-wrap gap-1.5">
                    {GOOD_TAGS.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          const next = new Set(fbTags);
                          next.has(tag) ? next.delete(tag) : next.add(tag);
                          setFbTags(next);
                        }}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                          fbTags.has(tag)
                            ? "bg-green-100 border-green-500 text-green-800"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Notes (optional)</p>
                  <textarea
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none"
                    rows={3}
                    placeholder="Specific moments or suggestions for the AI…"
                    value={fbNotes}
                    onChange={(e) => setFbNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t shrink-0 flex gap-2">
                <button
                  onClick={() => {
                    saveFeedbackMutation.mutate({
                      id: feedbackLog.id,
                      rating: fbRating,
                      tags: Array.from(fbTags),
                      notes: fbNotes,
                    });
                  }}
                  disabled={saveFeedbackMutation.isPending}
                  className="flex-1 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saveFeedbackMutation.isPending ? "Saving…" : "Save Feedback"}
                </button>
                <button
                  onClick={() => setFeedbackLog(null)}
                  className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
